import { min, max } from 'd3-array';
import { get, merge, set } from 'lodash';

import { types as eventTypes } from './enums/event';
import LineMarker from './markers/LineMarker';
import createUid from './util/uid';

/** Indicator objects are indicator parameters (period, smoothing) applied to one or more Markers */
export default class Indicator {
	/** @param {object} args - Custom args */
	constructor(args = {}) {
		/** @type {Panel} */
		this.panel = args.panel;
		/** @type {Modcharts} */
		this.core = this.panel.core;
		/** @type {number[]} */
		this.scaleUids = args.scaleUids || [this.panel.xAxis.primaryScaleUid, this.panel.yAxis.primaryScaleUid];
		/** @type {object} */
		this.params = this._getParams(args.params);
		/** @type {object} */
		this.markers = this._getMarkers();
		/** @type {object} */
		this.flags = {};
	}

	/**
	 * Returns a merged collection of default and custom params
	 * @param {object} args - Developer custom constructor args.
	 * @returns {object}
	 */
	_getParams(args) {
		const params = {
			extraInputs: [], // inputs not used by api (see mass index)
			id: 'indicator',
			inputs: [], // inputs used by chart api
			name: 'Chart Indicator',
			style: {},
			uid: createUid(),
			visible: true // Whether or not the indicator should be rendered
		};

		// merge derived indicator's default params into local params
		let paramsOut = { ...params, ...this._getDefaultParams() };

		// merge in styles from styleMap
		paramsOut.style = merge(paramsOut.style, this._getDefaultStyles(paramsOut.id));

		// merge in paramsCustom argument
		paramsOut = merge({}, paramsOut, args);

		return paramsOut;
	}

	/**
	 * Parse the styleMap for this indicator's styles
	 * @param {string} id - Indicator id (e.g. 'sma')
	 * @returns {object} A style object matching the styleMap for this indicator
	 */
	_getDefaultStyles(id = this.params.id) {
		return this.core.getStyle(`indicator.${id}`) || {};
	}

	/**
	 * Returns value of input name
	 * @param {string} name - Input name to search for.
	 */
	getInput(name) {
		for (const input of this.params.inputs) {
			if (input.name === name) {
				return input.value;
			}
		}

		for (const extraInput of this.params.extraInputs || []) {
			if (extraInput.name === name) {
				return extraInput.value;
			}
		}
	}

	/**
	 * Sets an input on this indicator
	 * @param {string} name - The input to set
	 * @param {string} value - The new input value
	 */
	setInput(name, value) {
		// Get indicator input with given name
		const indicatorInput = this.params.inputs.filter(row => row.name === name);

		// Set input to new value if that input was found above
		if (indicatorInput.length) {
			// Set input
			indicatorInput[0].value = value;

			// Fire update event
			this.core.eventEmitter.emit(
				eventTypes.INDICATOR_INPUT_UPDATE,
				{ indicator: this, name, value }
			);
		} else {
			// Figure out how to show error?
			this.core.warn(`Input ${name} not found`);
		}
	}

	/**
	 *  Sets a parameter on this indicator
	 * @param {string} name - The parameter to set
	 * @param {string} value - The new parameter value
	 */
	setParam(name, value) {
		// Get parameter with given name
		if (Object.prototype.hasOwnProperty.call(this.params, name)) {
			// Set param
			this.params[name] = value;

			// Fire update event
			this.core.eventEmitter.emit(
				eventTypes.INDICATOR_PARAM_UPDATE,
				{ indicator: this, name, value }
			);
		} else {
			this.core.warn(`Param ${name} not found`);
		}
	}

	/**
	 * Sets a style on this indicator
	 * @param {string} name - The style to set
	 * @param {string} value - The new style value
	 */
	setStyle(name, value) {
		if (this.getStyle(name) !== undefined) {
			let emitEvent = false;
			let updateStyle = false;

			// Make sure that numbers are saved as numbers, not strings
			if (typeof this.getStyle(name) === 'number') {
				// Only replace number type with new number
				if (typeof value !== 'number' && !isNaN(value)) {
					emitEvent = true;
					updateStyle = true;
					value = Number(value);
				} else {
					this.core.warn('Indicator.setStyle: Setting a non-numeric value where a numeric value is required is not allowed');
				}
			} else {
				emitEvent = true;
				updateStyle = true;
			}

			// Set the style
			if (updateStyle) set(this.params.style, name, value);

			// Fire update event
			if (emitEvent) {
				this.core.eventEmitter.emit(
					eventTypes.INDICATOR_STYLE_UPDATE,
					{ indicator: this, name, value }
				);
			}
		} else {
			this.core.warn(`Style ${name} not found`);
		}
	}

	/**
	 * Returns style info for the given selector from this indicator's params.style collection
	 * @param {string} selector - Selector to use when getting style (eg. 'panel.padding.right')
	 * @return {string|number} The style found in the styleMap
	 */
	getStyle(selector) {
		return get(this.params.style, selector);
	}

	/**
	 * Clips canvas context to panel size
	 * @param {CanvasRenderingContext2D} ctx - Canvas context
	 * @param {string} direction - Specify which area to clip - above or below value
	 * @param {int} value - The value limit of clip
	 * @return {CanvasRenderingContext2D}
	 */
	_clip(ctx, direction, value, dayLeft, dayRight) {
		ctx.save();
		ctx.beginPath();

		const top = this.panel.params.style.padding.top;
		const right = typeof dayRight === 'number' ? dayRight : this.panel.params.style.padding.left + this.panel.size.width;
		const bottom = this.panel.params.style.padding.top + this.panel.size.height;
		const left = typeof dayLeft === 'number' ? dayLeft : this.panel.params.style.padding.left;
		const y0 = this.yScale(value);

		if (direction === 'above') {
			ctx.moveTo(left, top);
			ctx.lineTo(right, top);
			ctx.lineTo(right, y0);
			ctx.lineTo(left, y0);
		} else {
			ctx.moveTo(left, y0);
			ctx.lineTo(right, y0);
			ctx.lineTo(right, bottom);
			ctx.lineTo(left, bottom);
		}

		ctx.closePath();

		ctx.clip();

		return ctx;
	}

	/**
	 * Restores context to preclip state
	 * @param {CanvasRenderingContext2D} ctx
	 * @return {CanvasRenderingContext2D}
	 */
	_clipEnd(ctx) {
		ctx.restore();
		return ctx;
	}

	/**
	 * Returns the default params for this type of indicator.
	 * @abstract
	 */
	_getDefaultParams() {
		console.warn('Required Indicator method _getDefaultParams not found');
	}

	/**
	 * Maps the current slice of indicator data to a one-dimensional array for
	 * use in generic marker rendering.
	 * @param {object[]} data - Data array
	 * @param {string} type - Key used to look up data in the array
	 * @returns {array[]} The resulting data array
	 */
	_getDataMap(data = [], type = this.params.id) {
		return data.map(d => [d.dateIndex, +d[type]]);
	}

	/**
	 * Returns indicator panel's xaxis d3-scale
	 * @returns {d3-scale}
	 */
	get xScale() {
		return this.panel.xAxis.getScale(this.scaleUids[0]);
	}

	/**
	 * Returns indicator panel's yaxis d3-scale
	 * @returns {d3-scale}
	 */
	get yScale() {
		return this.panel.yAxis.getScale(this.scaleUids[1]);
	}

	/**
	 * Returns the minimum value of all datasets for the indicator.
	 * @param {object[]} data - Data array
	 * @param {string} type - Key used to look up data in the array
	 * @returns {number} The minimum value found
	 */
	getRangeMin(data = [], type = this.params.id) {
		return min(data[type] || [], i => +i[type]);
	}

	/**
	 * Returns the maximum value of all datasets for the indicator.
	 * @param {object[]} data - Data array
	 * @param {string} type - Key used to look up data in the array
	 * @returns {number} The maximum value found
	 */
	getRangeMax(data = [], type = this.params.id) {
		return max(data[type] || [], i => +i[type]);
	}

	/**
	 * Helper function for getMinRange
	 * @return {number} The minimum value in the arguments
	 */
	_min() {
		let minVal = Infinity;

		for (const a of arguments) {
			minVal = Math.min(a, minVal);
		}

		return minVal;
	}

	/**
	 * Helper function for getMaxRange
	 * @return {number} The maximum value in the arguments
	 */
	_max() {
		let maxVal = -Infinity;

		for (const a of arguments) {
			maxVal = Math.max(a, maxVal);
		}

		return maxVal;
	}

	/**
	 * Return true if mouse coordinates intersect indicator.
	 * @return {Boolean} Whether or not the the mouse is within the indicator
	 * @abstract
	 */
	isWithin() {
		return false;
	}

	/**
	 * Checks for overlap between two boxes
	 * todo: performance improvement - convert to simple arrays instead of objects with x,y,width,height props
	 * @param {object} r1 - Rectangle 1
	 * @property {number} r1.x - Rectangle 1 x location
	 * @property {number} r1.y - Rectangle 1 y location
	 * @property {number} r1.width - Rectangle 1 width
	 * @property {number} r1.height - Rectangle 1 height
	 * @param {object} r2 - Rectangle 1
	 * @property {number} r2.x - Rectangle 1 x location
	 * @property {number} r2.y - Rectangle 1 y location
	 * @property {number} r2.width - Rectangle 1 width
	 * @property {number} r2.height - Rectangle 1 height
	 * @returns {boolean}
	 */
	_intersect(r1, r2) {
		if (
			r1.x < r2.x + r2.width &&
			r2.x < r1.x + r1.width &&
			r1.y < r2.y + r2.height
		) {
			return r2.y < r1.y + r1.height;
		} else {
			return false;
		}
	}

	/**
	 * Initialize and create references to this Indicator's Marker objects.
	 * @returns {object} Collection of indicator markers
	 */
	_getMarkers() {
		return { line: new LineMarker() };
	}

	/**
	 * Render this Indicator's Marker objects
	 * @abstract
	 */
	render() {
		console.warn('Required Indicator method render not found');
	}

	/**
	 * Cleanup before removing this indicator from panel's indicator collection
	 */
	remove() {
		// remove any associated flags
		Object.keys(this.flags).forEach(key => {
			const flag = this.flags[key];
			flag.remove();
			delete this.flags[key];
		});
	}

	/**
	 * Returns a gradient object given some colors and stops
	 * @param {CanvasRenderingContext2D} ctx - Canvas context
	 * @param {string} colorStart - CSS color value for the gradient start
	 * @param {string} colorStop - CSS color value for the gradient stop
	 * @param {number} x0 - X axis start location
	 * @param {number} y0 - Y axis start location
	 * @param {number} x1 - X axis end location
	 * @param {number} y1 - Y axis end location
	 * @returns {ICanvasGradient}
	 */
	_getLinearGradient(
		ctx,
		colorStart,
		colorStop,
		x0,
		y0,
		x1,
		y1
	) {
		const fill = ctx.createLinearGradient(x0, y0, x1, y1);
		fill.addColorStop(0, colorStart);
		fill.addColorStop(1, colorStop);
		return fill;
	}

	/**
	 * Gets a generic JSON object representation of this Indicator
	 * @returns {object} JSON representation of this indicator
	 */
	toJSON() {
		const json = {};
		const skipIndicatorParams = ['description']; // don't save these indicator params
		Object.keys(this.params).forEach(key => {
			const value = this.params[key];
			if (value === null) {
				return true;
			} else if (typeof value === 'object') {
				if (value.constructor === Array) {
					json[key] = [...[], ...value];
				} else {
					json[key] = { ...{}, ...value };
				}
			} else if (!skipIndicatorParams.includes(key)) {
				json[key] = value;
			}
		});
		return json;
	}

	/**
	 * Returns the first datapoint so that it can be passed back to ModchartsEngine for pan events.
	 * This prevents a jagged 'stairstep' jump when panning.  Only needed for a few indicators (OBV, A/D, ADL).
	 * @returns {object} Date and value of the first datapoint
	 */
	_getCumulativeAdjustment() {
		const { core } = this;
		const data = core.data[core.params.symbol][this.params.id][this.params.uid][0]; // this.data['36276'].adl['518b16a3'][0].adl
		// adjust the time offset so it is passed through as UTC (CH-352)
		const exchangeOffset = core.xref[core.params.symbol].utcOffset;
		const localOffset = data.date.getTimezoneOffset();
		const newDateIntraday = new Date(data.date.getTime() - ((localOffset + exchangeOffset) * 60000));
		const newDateInterday = new Date(data.date.getTime() - (localOffset * 60000));
		return {
			date: core.state.isIntraday ? newDateIntraday : newDateInterday,
			value: data[this.params.id]
		};
	}

	/**
	 * Returns a style value from either the style object, the style.sessionBreak object, or other applicable defaults.
	 * Fallback order for SessionBreak styles: bollinger.sma.sessionBreak > bollinger.sessionBreak > bollinger > defaults
	 * Fallback order for NormalSession styles: bollinger.sma > bollinger > defaults
	 * @returns {string} - Adjusted style value
	 * @property {string} key - Style key (eg. color, width, lineDash)
	 * @property {string} sessionType - Session type (eg. NormalSession, SessionBreak)
	 * @property {string} dataKey - Optional sub-series to use from data (eg. 'ema' in bollinger.ema)
	 */
	_getMarkerLineStyle(
		key = null,
		sessionType = 'NormalSession',
		dataKey = this.params.id
	) {
		const { style } = this.params;
		const { defaults } = this.core.styleMap.indicator;
		if (sessionType === 'SessionBreak') {
			if (
				style[dataKey] &&
				style[dataKey].sessionBreak &&
				style[dataKey].sessionBreak[key]
			) {
				// ex: bollinger.sma.sessionBreak.color
				return style[dataKey].sessionBreak[key];
			} else if (style.sessionBreak && style.sessionBreak[key]) {
				// ex: bollinger.sessionBreak.color
				return style.sessionBreak[key];
			} else if (style[dataKey] && style[dataKey][key]) {
				// ex: bollinger.sma.color
				return style[dataKey][key];
			} else if (style[key]) {
				// ex: bollinger.color
				return style[key];
			} else if (defaults.sessionBreak[key]) {
				// ex: defaults.sessionBreak.lineDash
				return defaults.sessionBreak[key];
			}
		} else if (style[dataKey] && style[dataKey][key]) {
			// ex: bollinger.sma.color
			return style[dataKey][key];
		} else if (style[key]) {
			// ex: bollinger.color
			return style[key];
		} else if (defaults[key]) {
			// ex: defaults.width
			return defaults[key];
		}
		return null;
	}

	/**
	 * A standardized render method for common line indicators.  Since most line indicators have similar render calls, this reduces redundant code.
	 * @returns {object} - Data series collection (eg. data.ema)
	 * @property {object} data - Data collection containing one or more data series
	 * @property {string} sessionType - Session type (eg. 'NormalSession', 'SessionBreak')
	 * @property {object} marker - Allow an alternate marker object to be used.  Usually it's markers.line.
	 * @property {object} dataKey - Which series to use from data (eg. 'ema')
	 * @property {array} dataMap - Allow an alternate dataMap to be passed.
	 * @property {string} styleKey - Which series to use from styles (eg. 'positive').  Usually the same as the dataKey.
	 * @property {string} color - Override color
	 * @property {number} width - Override width
	 * @property {string} lineDash - Override lineDash
	 * @property {boolean} isStepped - Specify the isStepped property needed for the marker render
	 */
	_renderLineMarker({
		data = {},
		sessionType = 'NormalSession',
		marker = this.markers.line,
		dataKey = this.params.id,
		dataMap = this._getDataMap(data, dataKey),
		styleKey = dataKey,
		color = this._getMarkerLineStyle('color', sessionType, styleKey),
		width = this._getMarkerLineStyle('width', sessionType, styleKey),
		lineDash = this._getMarkerLineStyle('lineDash', sessionType, styleKey),
		isStepped = false
	}) {
		marker.render(
			this.panel.rootContext,
			dataMap,
			this.xScale,
			this.yScale,
			color,
			width,
			lineDash,
			isStepped
		);
	}
}
